/**
 * Fixtures API Service
 *
 * Manages sports fixtures/matches - the core content driving user engagement and retention.
 * Fixtures represent both live events and video-on-demand (VOD) content, providing
 * the primary value proposition for sports streaming subscriptions.
 *
 * Key architectural decisions:
 * - Unified "streams" endpoint for both live and VOD (simplifies content discovery)
 * - No client-side entitlement filtering (server handles based on user JWT)
 * - Minimal caching for live content (15-second polling for real-time updates)
 * - Heavier caching for VOD content (5-minute staleTime reduces API load)
 *
 * Business value drivers:
 * - Live fixtures drive immediate engagement (FOMO - fear of missing out)
 * - VOD fixtures extend content value beyond live window (catch-up viewing)
 * - Fixture metadata powers discovery, recommendations, and personalisation
 * - Real-time updates maintain user trust during live events
 *
 * Performance considerations:
 * - Live fixtures require fresh data (scores, status, availability)
 * - VOD fixtures are stable (can be cached aggressively)
 * - Bulk fetch reduces API calls vs individual fixture requests
 * - React Query handles de-duplication and background refetching
 *
 * @see src/features/fixtures/CLAUDE.md - Fixture feature architecture
 * @see src/hooks/useFixtures.js - React Query implementation with polling
 * @see src/features/player/VideoPlayer.jsx - Bitmovin player integration
 */

import { getData } from './apiFetch';
import type { FixtureResponse, FixtureDetailsResponse } from '../types/api';

/**
 * Fetches all available fixtures for the current channel/club.
 *
 * Why "streams" endpoint name:
 * - Legacy naming from when all content was live streaming
 * - Now encompasses both live broadcasts and VOD replays
 * - Maintains backwards compatibility with mobile apps
 *
 * Business logic (server-side):
 * - Filters fixtures by user's subscription entitlements
 * - Applies geo-restrictions based on IP location
 * - Sorts by relevance (live first, then upcoming, then past)
 * - Includes metadata for UI rendering (thumbnails, badges, duration)
 *
 * Why bulk fetch pattern:
 * - Reduces API calls vs fetching individual fixtures
 * - Enables client-side filtering and sorting flexibility
 * - Supports offline-first mobile experiences (cache entire dataset)
 * - Improves perceived performance (instant navigation between fixtures)
 *
 * @returns {Promise<FixtureResponse[]>} Array of fixture objects with status, metadata, and entitlements
 * @throws {Error} Network errors, authentication failures, or server issues
 */
export async function getFixtures(): Promise<FixtureResponse[]> {
  const path = 'streams';
  // WHY: getData handles JWT token injection, retry logic, and error mapping
  // This centralises auth and ensures consistent error handling across all services
  const data = await getData<FixtureResponse[]>({ path });
  return data;
}

/**
 * Fetches detailed data for a specific fixture.
 *
 * Why individual fixture endpoint:
 * - Detailed player configuration (stream URLs, DRM tokens)
 * - Real-time entitlement check (subscription may change)
 * - Fresh statistics for live matches (scores, lineup changes)
 * - Personalised data (user's viewing history, bookmarks)
 *
 * Performance trade-offs:
 * - Additional API call when user selects fixture (slight delay)
 * - BUT ensures fresh entitlement check (prevents piracy)
 * - AND provides detailed data not needed in list view (reduces initial payload)
 *
 * Caching strategy:
 * - Live fixtures: No caching or very short (1-2 seconds)
 * - VOD fixtures: Standard 5-minute cache (metadata rarely changes)
 * - Player tokens: Never cached (security requirement)
 *
 * @param {string} id - Unique fixture identifier from CMS
 * @returns {Promise<FixtureDetailsResponse>} Detailed fixture with player configuration
 * @throws {Error} 404 if fixture not found, 403 if not entitled
 */
export async function getFixture(id: string): Promise<FixtureDetailsResponse> {
  // WHY: Using path template instead of query params for RESTful semantics
  // and better CDN caching (path-based cache keys more reliable)
  const path = `stream/${id}`;
  const data = await getData<FixtureDetailsResponse>({ path });
  return data;
}
